import { EnumFunnelTrackingCookies, getCookieWithFallback, removeCookieAndFallback, setCookieWithFallback, } from '@core/tracking/funnelTracking';
import { uuidv4 } from '@embed/helpers';
let hasRenewedThisSession = false;
let cachedGuestId = null;
let isGeneratingGuestId = false; // Flag to prevent race conditions
let pendingGuestId = null; // Stores the ID being generated
/**
 * Used for clearing guest id from storage
 */
export const clearGuestId = () => {
    // Clear cache when explicitly clearing guest ID
    cachedGuestId = null;
    hasRenewedThisSession = false;
    isGeneratingGuestId = false;
    pendingGuestId = null;
    removeCookieAndFallback(EnumFunnelTrackingCookies.FW_GUEST_ID);
};
/**
 * Validates guest ID format with stricter checks
 */
export const isValidGuestId = (guestId) => {
    // Basic checks
    if (!guestId || typeof guestId !== 'string') {
        return false;
    }
    // Trim whitespace and check for valid values
    const trimmedId = guestId.trim();
    if (!trimmedId || trimmedId === 'null' || trimmedId === 'undefined') {
        return false;
    }
    return true;
};
/**
 * Set guest id into storage
 */
export const setGuestId = (newGuestId) => {
    // Update cache when setting new guest ID
    cachedGuestId = newGuestId;
    setCookieWithFallback(EnumFunnelTrackingCookies.FW_GUEST_ID, newGuestId);
};
/**
 * Reading of guest_id from storage
 */
const fetchGuestId = () => getCookieWithFallback(EnumFunnelTrackingCookies.FW_GUEST_ID)?.value;
/**
 * Only public facing function to get guest id.
 * Resolve steps:
 *  1. Return cached value if available and valid
 *  2. get guest_id from storage
 *  3. create new guest_id and put to storage
 */
export const getGuestId = () => {
    // Return cached value if available and valid
    if (cachedGuestId && isValidGuestId(cachedGuestId)) {
        return cachedGuestId;
    }
    // If already generating, return the pending ID that all calls will share
    if (isGeneratingGuestId && pendingGuestId) {
        return pendingGuestId;
    }
    // If already generating but no pending ID yet, wait for it to be set
    if (isGeneratingGuestId) {
        // This shouldn't happen with synchronous execution, but fallback to cached or new ID
        return cachedGuestId || uuidv4();
    }
    isGeneratingGuestId = true;
    try {
        // Try to read from storage
        const persistedGuestId = fetchGuestId();
        if (persistedGuestId && isValidGuestId(persistedGuestId)) {
            // Cache the persisted guest ID
            cachedGuestId = persistedGuestId.trim(); // Ensure trimmed
            pendingGuestId = cachedGuestId; // Set pending ID for concurrent calls
            // If the persisted guest ID is valid, we should push its expiration date out for another year
            if (!hasRenewedThisSession) {
                setGuestId(cachedGuestId);
                hasRenewedThisSession = true;
            }
            return cachedGuestId;
        }
        else {
            // Clear invalid data from storage
            clearGuestId();
            isGeneratingGuestId = true; // Reset flag since clearGuestId cleared it
        }
        // Generate new guest ID if none exists or is invalid
        const guestId = uuidv4();
        pendingGuestId = guestId; // Set pending ID for concurrent calls
        setGuestId(guestId);
        return guestId;
    }
    finally {
        isGeneratingGuestId = false;
        pendingGuestId = null; // Clear pending ID after generation is complete
    }
};
